import { SortingFn } from '@tanstack/react-table';

/**
 * Numeric sorting function for TanStack Table
 * Handles numeric values including percentages (e.g., "0.01", "0.1", "10%")
 *
 * Features:
 * - Converts string numbers to actual numbers for proper comparison
 * - Handles percentage signs and other non-numeric characters
 * - Places empty/null values at the end
 * - Falls back to string comparison for non-numeric values
 */
export const numericSortingFn: SortingFn<any> = (rowA, rowB, columnId) => {
  const aValue = rowA.getValue(columnId);
  const bValue = rowB.getValue(columnId);

  // Handle null/undefined/empty values - put them at the end
  const aIsEmpty = aValue == null || aValue === '' || aValue === '-';
  const bIsEmpty = bValue == null || bValue === '' || bValue === '-';

  if (aIsEmpty && bIsEmpty) return 0;
  if (aIsEmpty) return 1; // Empty values go to the end
  if (bIsEmpty) return -1;

  // Convert to number, handling percentage signs and other non-numeric characters
  // Remove everything except digits, dots, and minus signs
  const numA = parseFloat(String(aValue).replace(/[^0-9.-]/g, ''));
  const numB = parseFloat(String(bValue).replace(/[^0-9.-]/g, ''));

  // If both are valid numbers, compare numerically
  if (!isNaN(numA) && !isNaN(numB)) {
    return numA - numB;
  }

  // If only one is numeric, prioritize it
  if (!isNaN(numA) && isNaN(numB)) return -1;
  if (isNaN(numA) && !isNaN(numB)) return 1;

  // Fallback to string comparison if neither is numeric
  return String(aValue).localeCompare(String(bValue));
};

/**
 * Date sorting function for TanStack Table
 * Handles date values including ISO date strings, timestamps, and Date objects
 *
 * Features:
 * - Converts date strings and timestamps to Date objects for proper comparison
 * - Handles ISO 8601 date strings (e.g., "2024-01-15T10:30:00Z")
 * - Handles numeric timestamps (milliseconds since epoch)
 * - Places empty/null values at the end
 * - Falls back to string comparison if dates cannot be parsed
 */
export const dateSortingFn: SortingFn<any> = (rowA, rowB, columnId) => {
  const aValue = rowA.getValue(columnId);
  const bValue = rowB.getValue(columnId);

  // Handle null/undefined/empty values - put them at the end
  const aIsEmpty = aValue == null || aValue === '' || aValue === '-';
  const bIsEmpty = bValue == null || bValue === '' || bValue === '-';

  if (aIsEmpty && bIsEmpty) return 0;
  if (aIsEmpty) return 1; // Empty values go to the end
  if (bIsEmpty) return -1;

  // Helper function to convert value to Date
  const toDate = (value: any): Date | null => {
    // If already a Date object
    if (value instanceof Date) {
      return isNaN(value.getTime()) ? null : value;
    }

    // If numeric, treat as timestamp (milliseconds)
    if (typeof value === 'number') {
      const date = new Date(value);
      return isNaN(date.getTime()) ? null : date;
    }

    // If string, try to parse as date
    if (typeof value === 'string') {
      const date = new Date(value);
      return isNaN(date.getTime()) ? null : date;
    }

    return null;
  };

  const dateA = toDate(aValue);
  const dateB = toDate(bValue);

  // If both are valid dates, compare by timestamp
  if (dateA && dateB) {
    return dateA.getTime() - dateB.getTime();
  }

  // If only one is a valid date, prioritize it
  if (dateA && !dateB) return -1;
  if (!dateA && dateB) return 1;

  // Fallback to string comparison if neither is a valid date
  return String(aValue).localeCompare(String(bValue));
};
